import { Content } from "antd/lib/layout/layout";
import React, { useEffect, useState } from "react";
import DashboardBreadcrumb from "../../components/layout/DashboardBreadcrumb";
import { useSelector } from "react-redux";
import GetMyPlansApi from "../../api/plans/GetMyPlansApi";
import { LIMIT_PER_PAGE } from "../../helpers/constants";
import { GetPlanTemplatesApi } from "../../api/plan-templates/GetPlanTemplatesApi";
import { Tabs } from "antd";
import TabPane from "antd/lib/tabs/TabPane";
import PageLoader from "../../components/sub/PageLoader";
import { WarningOutlined } from "@ant-design/icons";
import MyPlanTreeCards from "../../components/MyPlanTreeCard";
import { useLocation } from "react-router-dom";
import { getPluralString } from "../../helpers/wordHelpers";
import MyPlansTabContent from "../../components/MyPlansTabContent";
import PositionTasksTab from "../../components/PositionTasksTab";

export default function MyPlans() {
  const [data, setData] = useState([]);
  const [planCount, setPlanCount] = useState(0);
  const [canStart, setCanStart] = useState(false);
  const [canShowTab, setCanShowTab] = useState(false);
  const [page, setPage] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("");
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const proHeight = document.body.clientHeight;
  const curLocation = useLocation();
  const dataLength = data ? data.length : 0;
  const positionState = useSelector((state) => state.positionState.value);
  const [levelCounts, setLevelCounts] = useState({}); // { planLevelId: count }

  useEffect(() => {
    getTemplates();
  }, [perfCycleState, curLocation]);

  useEffect(() => {
    if (sortedPlanLevels && perfCycleState?.id && positionState?.id) {
      fetchLevelCounts();
    }
  }, [templates, perfCycleState, positionState]);

  useEffect(() => {
    // Set default active tab to first non-empty level, or tasks tab
    if (sortedPlanLevels && Object.keys(levelCounts).length > 0) {
      const firstNonEmpty = sortedPlanLevels.find(l => (levelCounts[l.id] || 0) > 0);
      if (firstNonEmpty) {
        setActiveTab(firstNonEmpty.id);
      } else if (sortedPlanLevels.some(l => l.tasks)) {
        setActiveTab("tasks-tab");
      }
    }
  }, [levelCounts]);

  useEffect(() => {
    if (activeTab && activeTab !== "tasks-tab") {
      setLoading(true);
      getMyPlanDetails(1, true, activeTab);
    }
  }, [activeTab]);

  async function fetchLevelCounts() {
    const counts = {};
    for (const level of sortedPlanLevels) {
      const res = await GetMyPlansApi(perfCycleState.id, level.id, 1, 1, positionState?.id);
      counts[level.id] = res.success ? (res.data?.totalCount || 0) : 0;
    }
    setLevelCounts(counts);
  }

  async function getTemplates() {
    const apiResponse = await GetPlanTemplatesApi();
    if (apiResponse.success) {
      setTemplates(apiResponse.data);
      setCanStart(true);
      setCanShowTab(true);
    }
  }

  const selectedTemplate = templates?.find(
    (template) => template.id === perfCycleState.template
  );

  const sortedPlanLevels = selectedTemplate?.planLevels.sort(
    (a, b) => a.order - b.order
  );

  async function getMyPlanDetails(num, newSet, planLevelId) {
    setTableLoading(true);
    const apiResponse = await GetMyPlansApi(
      perfCycleState.id,
      planLevelId,
      num,
      LIMIT_PER_PAGE,
      positionState?.id
    );
    if (apiResponse.success) {
      if (newSet) {
        setData(apiResponse.data?.plans);
        setPlanCount(apiResponse.data?.totalCount);
      } else {
        setData([...data, ...apiResponse.data?.plans]);
      }
      setPage(num);
    }
    setTableLoading(false);
    setLoading(false);
  }

  function loadMore(num) {
    getMyPlanDetails(num, false, activeTab);
  }

  const handleTabChange = (activeKey) => {
    setActiveTab(activeKey);
    setLoading(true); // Set loading to true when switching tabs
    const selectedPlanLevel = selectedTemplate?.planLevels.find(
      (planLevel) => planLevel.id === activeKey
    );
    if (selectedPlanLevel) {
      getMyPlanDetails(1, true, selectedPlanLevel.id); // Pass true for newSet and correct planLevelId
    }
  };

  function showEmptyPlans() {
    return (
      <div className="flex justify-center items-center w-full h-full">
        <div className="text-center">
          <div className="text-3xl text-gray-400">
            <WarningOutlined />
          </div>
          <div className="text-xl pri-font text-gray-400">
            Currently You Don't Have any Plans under this Performance Cycle!
          </div>
        </div>
      </div>
    );
  }

  const left = <div className="pri-font text-lg">My Plans</div>;

  return (
    <>
      <Content style={{ backgroundColor: "#fff" }}>
        <DashboardBreadcrumb left={left} />
        <div
          style={{
            height: proHeight - 122,
            overflow: "auto",
          }}
          className="custom-scroll"
        >
          {canStart ? (
            sortedPlanLevels ? (
              canShowTab ? (
                <Tabs
                  defaultActiveKey={activeTab}
                  centered
                  onChange={handleTabChange}
                  activeKey={activeTab}
                  tabBarGutter={60}
                >
                  {sortedPlanLevels.filter(planLevel => (levelCounts[planLevel.id] || 0) > 0).map((planLevel) => (
                    <TabPane tab={planLevel.name} key={planLevel.id}>
                      <div className="mb-8">
                        <MyPlansTabContent
                          planLevel={planLevel}
                          data={data}
                          page={page}
                          loading={loading}
                          tableLoading={tableLoading}
                          loadMore={(num) => loadMore(num)}
                          setPage={(num) => setPage(num)}
                          planCount={planCount}
                          perfCycleId={perfCycleState.id}
                          positionId={positionState?.id}
                          dataLength={dataLength}
                        />
                      </div>
                    </TabPane>
                  ))}
                  {sortedPlanLevels.some((level) => level.tasks) && (
                    <TabPane tab="Tasks" key="tasks-tab">
                      <div className="mb-8">
                        <PositionTasksTab
                          positionId={positionState?.id}
                          perfCycleId={perfCycleState.id}
                          allowHoldResume={true}
                        />
                      </div>
                    </TabPane>
                  )}
                </Tabs>
              ) : (
                showEmptyPlans()
              )
            ) : (
              showEmptyPlans()
            )
          ) : (
            <PageLoader />
          )}
        </div>
      </Content>
    </>
  );
}
